// A small chat-bubble notice in the bottom-right corner that survives page changes
// (e.g. "Don't forget to finish this contact's info" after creating a deal).

import { MessageCircle, X } from 'lucide-react';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

interface Toast {
  id: number;
  message: ReactNode;
  /** A link (to) or a button (onClick), e.g. Undo. */
  action?: { label: string; to?: string; onClick?: () => void };
  /** Disappears by itself after this many milliseconds (otherwise stays until dismissed). */
  autoHideMs?: number;
}

const ToastContext = createContext<(t: Omit<Toast, 'id'>) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = (id: number) => setToasts((ts) => ts.filter((t) => t.id !== id));
  const show = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts, { ...t, id }]);
    if (t.autoHideMs) setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), t.autoHideMs);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto rounded-2xl rounded-tr-sm border border-indigo-100 bg-white p-3.5 shadow-lg">
            <div className="flex items-start gap-2.5">
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
              <div className="flex-1 text-sm text-slate-700">{t.message}</div>
              <button onClick={() => dismiss(t.id)} className="text-slate-400 hover:text-slate-600" aria-label="Dismiss">
                <X className="h-4 w-4" />
              </button>
            </div>
            {t.action && (
              <div className="mt-2.5 flex justify-end">
                {t.action.to ? (
                  <Link
                    to={t.action.to}
                    onClick={() => dismiss(t.id)}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                  >
                    {t.action.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => (t.action?.onClick?.(), dismiss(t.id))}
                    className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                  >
                    {t.action.label}
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
